import { SlateData } from '@vimily/bonjoro-ui-components-v2/RichTextEditor';
import { UserSimple, Media, Transcript, Contact, LandingDomain } from '.';
import { Tag } from './tags';

export enum TestimonialRespondentStatus {
  InProgress = 'in_progress',
  Consented = 'consented',
  Dissented = 'dissented',
}

export enum TestimonialResponseType {
  Video = 'video',
  Text = 'text',
}

export enum FileRequestType {
  CompanyLogo = 'company_logo',
  ProfilePhoto = 'profile_photo',
  CustomFile = 'custom_file',
}

export enum LinkRequestType {
  Facebook = 'facebook',
  Twitter = 'twitter',
  LinkedIn = 'linkedIn',
  Website = 'website',
  CustomLink = 'custom_link',
}

export enum PublishType {
  Quote = 'quote',
  Slider = 'slider',
  WallOfLove = 'wall-of-love',
}

export enum QuoteLayoutType {
  VideoAndText = 'video_and_text',
  TextOnly = 'text_only',
}

export enum CommonLayoutType {
  AllTestimonials = 'all_testimonials',
  StarredTestimonials = 'starred_testimonials',
  ByTag = 'by_tag',
}

/**
 * TODO: https://bonjoro.atlassian.net/browse/BJ-2842
 * Remove on FE integration as we may not need this anymore.
 */
export enum UserTagType {
  All = 'All',
  Star = 'Star',
  Tag = 'Tag',
}

export enum TestimonialBackgroundType {
  Color = 'color',
  Image = 'image',
}

export interface TestimonialStyleProps {
  background_type?: TestimonialBackgroundType;
  background_color?: string;
  primary_color?: string;
  secondary_color?: string;
  cta_corner_radius?: number;
  text_color?: string;
  font_family?: string;
  attachments?: {
    box_color?: string;
  };
  closing_message?: {
    box_color?: string;
  };
  outro?: {
    box_color?: string;
  };
}

export interface TestimonialMetadata {
  newly_created?: boolean;
  style?: TestimonialStyleProps;
}

export interface Testimonial {
  uuid: string;
  name: string;
  user: UserSimple;
  questions: TestimonialQuestion[];
  landing_page: {
    background: Media | null;
    logo: Media | null;
  };
  text: TestimonialText;
  request_position_title: boolean;
  requests: {
    links: TestimonialRequest[];
    files: TestimonialRequest[];
  };
  response_count: number;
  invite_link: string;
  metadata?: TestimonialMetadata;
  tags?: ResourceTag[];
  updated_at: string;
  domain?: LandingDomain;
}

export interface TestimonialRespondentMetaData {
  letsGo?: DeviceSpecs;
  recordResponse?: AudioVideoDeviceSpecs;
}

export interface TestimonialRespondent {
  uuid: string;
  name: string;
  email: string;
  image: string;
  status: TestimonialRespondentStatus;
  metadata?: TestimonialRespondentMetaData;
  viewed: boolean;
  user: UserSimple;
  position?: string;
  request_position_title?: boolean;
  questions: TestimonialQuestion[];
  requests: {
    links: TestimonialRequest[];
    files: TestimonialRequest[];
  };
  updated_at: string;
  created_at: string;
  submitted_at: string;
  consent_updated_at?: string;
  tags: Tag[];
  profile: Contact;
}

export interface TestimonialQuestion {
  uuid: string;
  bg_color: string;
  question?: string;
  question_rich: SlateData[];
  response_types: TestimonialResponseType[];
  response?: TestimonialResponse;
  response_count?: number;
}

export interface TestimonialResponse {
  uuid: string;
  type: TestimonialResponseType;
  text_response?: string;
  text_response_rich?: SlateData[];
  thumb_url?: string;
  thumb_full_url?: string;
  video_url?: string | null;
  created_at?: string;
  updated_at?: string;
  tags?: ResourceTag[];
  is_locked?: boolean;
  transcript?: Transcript;
}

export interface ResourceTag {
  uuid: string;
  resource: Record<string, unknown>;
  tag: Tag;
  updated_at: string;
}

export interface TestimonialRespondentsMedia {
  uuid: string;
  testimonial: Pick<Testimonial, 'uuid' | 'name'>;
  respondent: Partial<TestimonialRespondent>;
  respondent_uuid: string;
  question_uuid: string;
  viewed: boolean;
  is_locked?: boolean;
  thumb_url: string;
  video_length: number;
  video_url: string;
  video_response_count: number;
  text_response: string;
  text_response_rich: SlateData[];
  text_response_count: number;
  updated_at: string;
  created_at: string;
  tags: ResourceTag[];
  type: TestimonialResponseType;
  transcript?: Transcript;
}

export interface TestimonialRequest {
  uuid?: string;
  label: string;
  type: FileRequestType | LinkRequestType;
  response?: TestimonialAttachment;
}

export type TestimonialAttachment = {
  uuid: string;
  type: FileRequestType | LinkRequestType;
  text_response: string | null;
  media_url: string;
};

export type TestimonialText = {
  intro?: string;
  intro_rich: SlateData[];
  closing_message_rich: SlateData[];
  consent_statement_rich: SlateData[];
  outro?: string;
  outro_rich: SlateData[];
  instruction?: string;
  instruction_rich: SlateData[];
};

export enum TestimonialAssetPage {
  All = 'all',
  Responses = 'responses',
  Uploads = 'uploads',
}
